import { useEffect, useState } from "react";
import { GripVertical, Loader2, Pencil, Target, Trash2 } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import type { AvatarUpdateInput } from "@/lib/composerCore";

export type AvatarDraftState = {
  name: string;
  experienceLevel: string;
  summary: string;
  painPoints: string;
  desires: string;
  toneGuidance: string;
  vocabularyAssumptions: string;
  messagingPosture: string;
  voiceModeAWeight: number;
  toneAdjustments: string;
  vocabularyAdjustments: string;
};

type AvatarSummary = {
  id: string;
  name: string;
  experience_level: string | null;
  summary: string | null;
  tone_guidance: string | null;
  pain_points: string[];
  desires: string[];
  voice_mode_a_weight?: number | null;
  vocabulary_assumptions?: string | null;
  messaging_posture?: string | null;
  tone_adjustments?: string | null;
  vocabulary_adjustments?: string | null;
};

type Props = {
  avatars: AvatarSummary[];
  avatarDraft: AvatarDraftState;
  setAvatarDraft: React.Dispatch<React.SetStateAction<AvatarDraftState>>;
  creatingAvatar: boolean;
  onCreate: () => void;
  /** Workspace voice-mode labels (fallback to Primary Voice / Teaching). */
  voiceModeALabel?: string;
  voiceModeBLabel?: string;
  /** When false, the per-avatar A/B mix is hidden and treated as 100% Voice A. */
  dualVoiceModesEnabled?: boolean;
  /** Persist edits to a single avatar. */
  onEditAvatar?: (avatarId: string, values: AvatarUpdateInput) => Promise<void>;
  /** Hard-delete an avatar. */
  onDeleteAvatar?: (avatarId: string) => Promise<void>;
  /** Persist a new ordering of avatars (array of ids in new order). */
  onReorderAvatars?: (orderedIds: string[]) => Promise<void>;
};

const splitLines = (value: string): string[] =>
  value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

const avatarToDraft = (a: AvatarSummary): AvatarDraftState => ({
  name: a.name ?? "",
  experienceLevel: a.experience_level ?? "",
  summary: a.summary ?? "",
  painPoints: (a.pain_points ?? []).join("\n"),
  desires: (a.desires ?? []).join("\n"),
  toneGuidance: a.tone_guidance ?? "",
  vocabularyAssumptions: a.vocabulary_assumptions ?? "",
  messagingPosture: a.messaging_posture ?? "",
  voiceModeAWeight: a.voice_mode_a_weight ?? 50,
  toneAdjustments: a.tone_adjustments ?? "",
  vocabularyAdjustments: a.vocabulary_adjustments ?? "",
});

type SortableAvatarCardProps = {
  avatar: AvatarSummary;
  dualVoiceModesEnabled: boolean;
  voiceModeALabel: string;
  voiceModeBLabel: string;
  onEdit: () => void;
  onDelete: () => void;
  canMutate: boolean;
};

const SortableAvatarCard = ({
  avatar,
  dualVoiceModesEnabled,
  voiceModeALabel,
  voiceModeBLabel,
  onEdit,
  onDelete,
  canMutate,
}: SortableAvatarCardProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: avatar.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const aw = avatar.voice_mode_a_weight ?? 50;

  return (
    <div ref={setNodeRef} style={style} className="space-y-3 rounded-md border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          {canMutate && (
            <button
              type="button"
              aria-label="Reorder avatar"
              className="mt-0.5 cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-4 w-4" />
            </button>
          )}
          <div className="min-w-0">
            <h3 className="font-medium truncate">{avatar.name}</h3>
            <p className="text-sm text-muted-foreground truncate">
              {avatar.experience_level || "No level assigned"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {canMutate && (
            <>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onEdit} aria-label="Edit avatar">
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={onDelete}
                aria-label="Delete avatar"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>

      <Section label="Summary">
        {avatar.summary ? (
          <p className="text-sm whitespace-pre-wrap">{avatar.summary}</p>
        ) : (
          <EmptyDash />
        )}
      </Section>

      <Section label="Voice / Mix">
        {dualVoiceModesEnabled ? (
          <p className="text-sm">
            {voiceModeALabel} {aw}% &nbsp;/&nbsp; {voiceModeBLabel} {100 - aw}%
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">{voiceModeALabel} (single voice mode)</p>
        )}
      </Section>

      <Section label="Pain points">
        {avatar.pain_points.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {avatar.pain_points.map((item) => (
              <Badge key={`pp-${item}`} variant="secondary">{item}</Badge>
            ))}
          </div>
        ) : (
          <EmptyDash />
        )}
      </Section>

      <Section label="Desires">
        {avatar.desires.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {avatar.desires.map((item) => (
              <Badge key={`d-${item}`} variant="outline">{item}</Badge>
            ))}
          </div>
        ) : (
          <EmptyDash />
        )}
      </Section>

      <Section label="Tone guidance">
        <Paragraph value={avatar.tone_guidance} />
      </Section>
      <Section label="Tone adjustments">
        <Paragraph value={avatar.tone_adjustments ?? null} />
      </Section>
      <Section label="Vocabulary assumptions">
        <Paragraph value={avatar.vocabulary_assumptions ?? null} />
      </Section>
      <Section label="Vocabulary adjustments">
        <Paragraph value={avatar.vocabulary_adjustments ?? null} />
      </Section>
      <Section label="Messaging posture">
        <Paragraph value={avatar.messaging_posture ?? null} />
      </Section>
    </div>
  );
};

const Section = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1">
    <p className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">{label}</p>
    {children}
  </div>
);

const EmptyDash = () => <p className="text-sm text-muted-foreground">—</p>;

const Paragraph = ({ value }: { value: string | null | undefined }) =>
  value && value.trim().length > 0 ? (
    <p className="text-sm whitespace-pre-wrap">{value}</p>
  ) : (
    <EmptyDash />
  );

type AvatarFormFieldsProps = {
  draft: AvatarDraftState;
  setDraft: React.Dispatch<React.SetStateAction<AvatarDraftState>>;
  dualVoiceModesEnabled: boolean;
  voiceModeALabel: string;
  voiceModeBLabel: string;
};

const AvatarFormFields = ({
  draft,
  setDraft,
  dualVoiceModesEnabled,
  voiceModeALabel,
  voiceModeBLabel,
}: AvatarFormFieldsProps) => {
  const aWeight = draft.voiceModeAWeight;
  const bWeight = 100 - aWeight;
  return (
    <div className="space-y-3">
      <Input placeholder="Name" value={draft.name} onChange={(e) => setDraft((c) => ({ ...c, name: e.target.value }))} />
      <Input placeholder="Experience level" value={draft.experienceLevel} onChange={(e) => setDraft((c) => ({ ...c, experienceLevel: e.target.value }))} />
      <Textarea placeholder="Summary" value={draft.summary} onChange={(e) => setDraft((c) => ({ ...c, summary: e.target.value }))} />
      <Textarea placeholder="Pain points — one per line" value={draft.painPoints} onChange={(e) => setDraft((c) => ({ ...c, painPoints: e.target.value }))} />
      <Textarea placeholder="Desires — one per line" value={draft.desires} onChange={(e) => setDraft((c) => ({ ...c, desires: e.target.value }))} />

      <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
        <p className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">Voice / Mix</p>
        {dualVoiceModesEnabled ? (
          <>
            <div className="flex items-center justify-between text-sm font-medium">
              <span>{voiceModeALabel}</span>
              <span>{voiceModeBLabel}</span>
            </div>
            <Slider
              value={[aWeight]}
              min={0}
              max={100}
              step={5}
              onValueChange={(vals) => setDraft((c) => ({ ...c, voiceModeAWeight: vals[0] ?? 50 }))}
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{aWeight}%</span>
              <span>{bWeight}%</span>
            </div>
          </>
        ) : (
          <p className="text-xs text-muted-foreground">
            Single voice mode is active. Enable a second mode in Voice → Master Voice Document to blend per-avatar.
          </p>
        )}
      </div>

      <Textarea placeholder="Tone guidance" value={draft.toneGuidance} onChange={(e) => setDraft((c) => ({ ...c, toneGuidance: e.target.value }))} />
      <Textarea placeholder="Tone adjustments (on top of master voice)" value={draft.toneAdjustments} onChange={(e) => setDraft((c) => ({ ...c, toneAdjustments: e.target.value }))} />
      <Textarea placeholder="Vocabulary assumptions" value={draft.vocabularyAssumptions} onChange={(e) => setDraft((c) => ({ ...c, vocabularyAssumptions: e.target.value }))} />
      <Textarea placeholder="Vocabulary adjustments (on top of master voice)" value={draft.vocabularyAdjustments} onChange={(e) => setDraft((c) => ({ ...c, vocabularyAdjustments: e.target.value }))} />
      <Textarea placeholder="Messaging posture" value={draft.messagingPosture} onChange={(e) => setDraft((c) => ({ ...c, messagingPosture: e.target.value }))} />
    </div>
  );
};

export function AvatarsPanel({
  avatars,
  avatarDraft,
  setAvatarDraft,
  creatingAvatar,
  onCreate,
  voiceModeALabel = "Primary Voice",
  voiceModeBLabel = "Teaching",
  dualVoiceModesEnabled = false,
  onEditAvatar,
  onDeleteAvatar,
  onReorderAvatars,
}: Props) {
  // Local ordering for instant DnD UX, kept in sync with snapshot.
  const [ordered, setOrdered] = useState<AvatarSummary[]>(avatars);
  useEffect(() => {
    setOrdered(avatars);
  }, [avatars]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ordered.findIndex((a) => a.id === active.id);
    const newIndex = ordered.findIndex((a) => a.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(ordered, oldIndex, newIndex);
    setOrdered(next);
    void onReorderAvatars?.(next.map((a) => a.id));
  };

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<AvatarDraftState | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const openEdit = (avatar: AvatarSummary) => {
    setEditingId(avatar.id);
    setEditDraft(avatarToDraft(avatar));
  };

  const submitEdit = async () => {
    if (!editingId || !editDraft || !onEditAvatar) return;
    setSavingEdit(true);
    try {
      await onEditAvatar(editingId, {
        name: editDraft.name,
        experienceLevel: editDraft.experienceLevel,
        summary: editDraft.summary,
        painPoints: splitLines(editDraft.painPoints),
        desires: splitLines(editDraft.desires),
        toneGuidance: editDraft.toneGuidance,
        vocabularyAssumptions: editDraft.vocabularyAssumptions,
        messagingPosture: editDraft.messagingPosture,
        voiceModeAWeight: editDraft.voiceModeAWeight,
        toneAdjustments: editDraft.toneAdjustments,
        vocabularyAdjustments: editDraft.vocabularyAdjustments,
      });
      setEditingId(null);
      setEditDraft(null);
    } catch {
      // toast handled upstream
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingId || !onDeleteAvatar) return;
    setDeleting(true);
    try {
      await onDeleteAvatar(deletingId);
      setDeletingId(null);
    } catch {
      // toast handled upstream
    } finally {
      setDeleting(false);
    }
  };

  const canMutate = Boolean(onEditAvatar && onDeleteAvatar && onReorderAvatars);
  const deletingAvatar = ordered.find((a) => a.id === deletingId) ?? null;

  return (
    <div className="p-4">
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_400px]">
        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle>Audience Avatars</CardTitle>
            <CardDescription>
              {dualVoiceModesEnabled
                ? "Each avatar blends the two voice modes and can add tone or vocabulary adjustments on top of the master voice profile."
                : "Each avatar can layer tone or vocabulary adjustments on top of the master voice profile."}
              {canMutate ? " Drag the grip to reorder." : ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={ordered.map((a) => a.id)} strategy={verticalListSortingStrategy}>
                <div className="grid gap-4 lg:grid-cols-2">
                  {ordered.map((avatar) => (
                    <SortableAvatarCard
                      key={avatar.id}
                      avatar={avatar}
                      dualVoiceModesEnabled={dualVoiceModesEnabled}
                      voiceModeALabel={voiceModeALabel}
                      voiceModeBLabel={voiceModeBLabel}
                      onEdit={() => openEdit(avatar)}
                      onDelete={() => setDeletingId(avatar.id)}
                      canMutate={canMutate}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </CardContent>
        </Card>

        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle>Add Avatar</CardTitle>
            <CardDescription>
              {dualVoiceModesEnabled
                ? "Set how this avatar blends your two voice modes, plus any per-avatar tone or vocabulary tweaks."
                : "Add per-avatar tone or vocabulary tweaks on top of your master voice."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <AvatarFormFields
              draft={avatarDraft}
              setDraft={setAvatarDraft}
              dualVoiceModesEnabled={dualVoiceModesEnabled}
              voiceModeALabel={voiceModeALabel}
              voiceModeBLabel={voiceModeBLabel}
            />
            <Button className="w-full" onClick={onCreate} disabled={creatingAvatar || !avatarDraft.name.trim()}>
              {creatingAvatar ? <Loader2 className="h-4 w-4 animate-spin" /> : <Target className="h-4 w-4" />}
              Add avatar
            </Button>
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={editingId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditingId(null);
            setEditDraft(null);
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit avatar</DialogTitle>
            <DialogDescription>Update the fields below and save.</DialogDescription>
          </DialogHeader>
          {editDraft && (
            <AvatarFormFields
              draft={editDraft}
              setDraft={setEditDraft as React.Dispatch<React.SetStateAction<AvatarDraftState>>}
              dualVoiceModesEnabled={dualVoiceModesEnabled}
              voiceModeALabel={voiceModeALabel}
              voiceModeBLabel={voiceModeBLabel}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditingId(null); setEditDraft(null); }} disabled={savingEdit}>
              Cancel
            </Button>
            <Button onClick={() => void submitEdit()} disabled={savingEdit || !editDraft?.name.trim()}>
              {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deletingId !== null} onOpenChange={(open) => { if (!open) setDeletingId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete avatar?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingAvatar
                ? `Delete avatar "${deletingAvatar.name}"? This cannot be undone.`
                : "This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); void confirmDelete(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
